import { useCallback, useEffect, useState } from "react";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { PAYMENTS_BY_STORE_AND_PAYMENT_TYPE } from "src/constants/queries";
import type { PaymentNode, PaymentPageInfo } from "../types";

const PAGE_SIZE = 10;

export type PaymentTypeFilter = "CARD" | "CASH" | "All";

/** Backend enum value for the API (All -> ALL). */
type PaymentTypeApi = "CARD" | "CASH" | "ALL";

export interface TransactionsPaymentsParams {
  storeCorrelationId: string | null;
  startTimeInEpochMilli: number;
  endTimeInEpochMilli: number;
  paymentTypeFilter: PaymentTypeFilter;
}

export interface UseTransactionsPaymentsResult {
  payments: PaymentNode[];
  pageInfo: PaymentPageInfo;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalPayments: number;
  start: number;
  end: number;
  loadNextPage: () => void;
  loadPrevPage: () => void;
  goToPage: (page: number) => void;
  canGoToPage: (page: number) => boolean;
  refetch: () => void;
}

function paymentTypeToApi(filter: PaymentTypeFilter): PaymentTypeApi {
  if (filter === "All") return "ALL";
  return filter;
}

function fetchPayments(
  axBe: ReturnType<typeof useAxiosInterceptor>["axBe"],
  storeCorrelationId: string,
  startTimeInEpochMilli: number,
  endTimeInEpochMilli: number,
  paymentType: PaymentTypeApi,
  cursor: { first?: number; last?: number; after?: string | null; before?: string | null }
): Promise<{ pageInfo: PaymentPageInfo; edges: { cursor: string; node: PaymentNode }[] }> {
  const variables = {
    storeCorrelationId,
    startTimeInEpochMilli,
    endTimeInEpochMilli,
    paymentType,
    first: cursor.before != null ? undefined : (cursor.first ?? PAGE_SIZE),
    last: cursor.before != null ? (cursor.last ?? PAGE_SIZE) : undefined,
    after: cursor.after ?? undefined,
    before: cursor.before ?? undefined,
  };
  return axBe
    .post<{
      data?: {
        paymentsByStoreCorrelationIdAndPaymentType?: {
          paymentPageInfo?: PaymentPageInfo;
          edges?: Array<{ cursor: string; node: PaymentNode }>;
        };
      };
      errors?: Array<{ message?: string }>;
    }>(GRAPHQL_ENDPOINT, { query: PAYMENTS_BY_STORE_AND_PAYMENT_TYPE, variables })
    .then(({ data }) => {
      const err = data?.errors?.[0];
      if (err) throw new Error(err.message ?? "Failed to load payments");
      const conn = data?.data?.paymentsByStoreCorrelationIdAndPaymentType;
      const pageInfo: PaymentPageInfo = {
        startCursor: conn?.paymentPageInfo?.startCursor ?? null,
        endCursor: conn?.paymentPageInfo?.endCursor ?? null,
        hasPreviousPage: conn?.paymentPageInfo?.hasPreviousPage ?? false,
        hasNextPage: conn?.paymentPageInfo?.hasNextPage ?? false,
        totalNumberOfPages: conn?.paymentPageInfo?.totalNumberOfPages,
        totalNumberOfPayments: conn?.paymentPageInfo?.totalNumberOfPayments,
      };
      const edges = conn?.edges ?? [];
      return { pageInfo, edges };
    });
}

/** Build page list for pagination (ellipsis when many pages). */
function getPaginationPages(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  if (currentPage >= totalPages - 3) return [1, "ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

export function useTransactionsPayments(params: TransactionsPaymentsParams): UseTransactionsPaymentsResult {
  const { storeCorrelationId, startTimeInEpochMilli, endTimeInEpochMilli, paymentTypeFilter } = params;
  const { axBe } = useAxiosInterceptor();

  const [payments, setPayments] = useState<PaymentNode[]>([]);
  const [pageInfo, setPageInfo] = useState<PaymentPageInfo>({
    startCursor: null,
    endCursor: null,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [cursorCache, setCursorCache] = useState<Record<number, string>>({});

  const fetchPage = useCallback(
    async (cursor: { after?: string | null; before?: string | null }, pageNum: number) => {
      if (!storeCorrelationId) return;
      setLoading(true);
      setError(null);
      try {
        const paymentType = paymentTypeToApi(paymentTypeFilter);
        const { pageInfo: pi, edges } = await fetchPayments(
          axBe,
          storeCorrelationId,
          startTimeInEpochMilli,
          endTimeInEpochMilli,
          paymentType,
          {
            first: cursor?.before != null ? undefined : PAGE_SIZE,
            last: cursor?.before != null ? PAGE_SIZE : undefined,
            after: cursor?.after ?? undefined,
            before: cursor?.before ?? undefined,
          }
        );
        setPayments(edges.map((e) => e.node));
        setPageInfo(pi);
        if (pi.endCursor != null) setCursorCache((prev) => ({ ...prev, [pageNum + 1]: pi.endCursor! }));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load transactions");
        setPayments([]);
      } finally {
        setLoading(false);
      }
    },
    [storeCorrelationId, startTimeInEpochMilli, endTimeInEpochMilli, paymentTypeFilter, axBe]
  );

  const refetch = useCallback(() => {
    if (!storeCorrelationId) return;
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
    setCursorCache({});
    fetchPage({ after: null, before: null }, 1);
  }, [storeCorrelationId, fetchPage]);

  useEffect(() => {
    setAfter(null);
    setBefore(null);
    setCurrentPage(1);
    setCursorCache({});
  }, [storeCorrelationId, startTimeInEpochMilli, endTimeInEpochMilli, paymentTypeFilter]);

  useEffect(() => {
    if (!storeCorrelationId) return;
    fetchPage({ after, before }, currentPage);
    // cursorCache intentionally omitted so setting cache after fetch doesn't re-trigger effect
  }, [storeCorrelationId, currentPage, after, before, fetchPage]);

  const totalPayments = pageInfo.totalNumberOfPayments ?? 0;
  const totalPages = Math.max(1, pageInfo.totalNumberOfPages ?? Math.ceil(totalPayments / PAGE_SIZE));
  const start = totalPayments === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = totalPayments === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, totalPayments);

  const loadNextPage = useCallback(() => {
    if (loading) return;
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      setBefore(null);
      setAfter(pageInfo.endCursor);
      setCurrentPage((p) => p + 1);
    }
  }, [loading, pageInfo.hasNextPage, pageInfo.endCursor]);

  const loadPrevPage = useCallback(() => {
    if (loading) return;
    if (currentPage <= 1) return;
    if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
      setAfter(null);
      setBefore(pageInfo.startCursor);
      setCurrentPage((p) => Math.max(1, p - 1));
    }
  }, [loading, currentPage, pageInfo.hasPreviousPage, pageInfo.startCursor]);

  const goToPage = useCallback(
    (page: number) => {
      if (loading) return;
      const target = Math.max(1, Math.min(page, totalPages));
      if (target === currentPage) return;
      if (target === 1) {
        setAfter(null);
        setBefore(null);
        setCurrentPage(1);
        return;
      }
      const cursor = cursorCache[target];
      if (cursor != null) {
        setAfter(cursor);
        setBefore(null);
        setCurrentPage(target);
      } else if (target === currentPage + 1 && pageInfo.endCursor) {
        setAfter(pageInfo.endCursor);
        setBefore(null);
        setCurrentPage(target);
      }
    },
    [loading, currentPage, totalPages, cursorCache, pageInfo.endCursor]
  );

  const canGoToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return false;
      if (page === 1) return true;
      if (page === currentPage + 1) return pageInfo.hasNextPage ?? false;
      return cursorCache[page] != null;
    },
    [totalPages, currentPage, pageInfo.hasNextPage, cursorCache]
  );

  return {
    payments,
    pageInfo,
    loading,
    error,
    currentPage,
    totalPages,
    totalPayments,
    start,
    end,
    loadNextPage,
    loadPrevPage,
    goToPage,
    canGoToPage,
    refetch,
  };
}

export { getPaginationPages };
